import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewEncapsulation,
    computed,
    effect,
    forwardRef,
    input,
    model,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { rxHostPressedListener } from '@frontend/ui/rx-host-listener';
import { filter } from 'rxjs';

export const DS_CHECKBOX_SIZE_ARRAY = ['medium', 'large'] as const;
export type DsCheckboxSize = (typeof DS_CHECKBOX_SIZE_ARRAY)[number];
export const DS_CHECKBOX_POSITION_ARRAY = ['left', 'right'] as const;
export type DsCheckboxLabelPosition = (typeof DS_CHECKBOX_POSITION_ARRAY)[number];

@Component({
    selector: 'ds-checkbox',
    standalone: true,
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    host: {
        'class': 'ds-checkbox',
        '[class]': 'hostClass()',
        '[class.ds-checkbox-checked]': 'checked()',
        '[class.ds-checkbox-indeterminate]': 'indeterminate()',
        '[class.ds-checkbox-disabled]': 'disabled()',
        '[class.ds-checkbox-inverse]': 'inverse()',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DsCheckbox),
            multi: true,
        },
    ],
})
export class DsCheckbox implements ControlValueAccessor {
    protected readonly checkbox = viewChild<ElementRef<HTMLInputElement>>('checkbox');

    public readonly checked = model(false);
    public readonly indeterminate = model(false);
    public readonly disabled = model(false);

    public readonly size = input<DsCheckboxSize>('large');
    public readonly inverse = input(false);
    public readonly interactive = input(true);
    public readonly id = input<string>();
    public readonly name = input<string>();
    public readonly required = input(false);
    public readonly ariaLabel = input<string | null>(null);
    public readonly ariaLabelledby = input<string | null>(null);
    public readonly ariaDescribedby = input<string | null>(null);
    public readonly labelPosition = input<DsCheckboxLabelPosition>('right');

    private _onChange: (value: any) => void = () => {};
    private _onTouched: () => void = () => {};

    protected onTouched(): void {
        this._onTouched();
    }
    protected readonly hostClass = computed(() => `ds-checkbox-${this.size()} ds-checkbox-label-${this.labelPosition()}`);

    constructor() {
        rxHostPressedListener()
            .pipe(
                takeUntilDestroyed(),
                filter(() => this.interactive()),
            )
            .subscribe(() => {
                this.toggle();
            });

        effect(() => {
            if (this.checkbox()) {
                // Setting it to the object is required, as browser's check state does not reflect it
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                Object.assign(this.checkbox()!.nativeElement, {
                    indeterminate: this.indeterminate(),
                    checked: this.checked(),
                    disabled: this.disabled(),
                });
            }
        });
    }

    toggle() {
        if (this.disabled()) {
            return;
        }

        if (this.indeterminate()) {
            this.indeterminate.set(false);
            this.checked.set(true);
        } else {
            this.checked.update((v) => !v);
        }

        this._onChange(this.checked());
    }

    writeValue(value: any): void {
        this.checked.set(!!value);
        this.indeterminate.set(false);
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
    }
}
